import DatePicker from 'react-datepicker'
import { Form } from 'react-bootstrap'

import { SelectDropdown, SelectOption } from '../../../components/SelectDropdown/SelectDropdown'
import { MultiSelectDropdown } from '../../../components/MultiSelectDropdown/MultiSelectDropdown'

import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  resetDocumentFilters,
  selectDocumentContactFilter,
  selectDocumentCreatedByFilter,
  selectDocumentEndDateFilter,
  selectDocumentStartDateFilter,
  selectDocumentTypeFilter,
  setDocumentContactFilter,
  setDocumentCreatedByFilter,
  setDocumentEndDateFilter,
  setDocumentStartDateFilter,
  setDocumentTypeFilter,
} from '../../../store/processDocument/documentFilter'

import { getUserDisplayName } from '../../../apps/professional-app/fa/common/aifeatures/utils'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { useGetExternalUsersQuery } from '../../../store/externalUser/externalUser'
import { selectDocumentTypes } from '../../../store/processDocument/documentTypes'
import { useGetAllUsersQuery } from '../../../store/user/user'
import { skipToken } from '@reduxjs/toolkit/query'
import { useDocumentSearchParams } from '../DocumentUpload/hooks'
import { ALL_UPLOADERS_FILTER_ID } from '../utils'
import { useState } from 'react'

const _ALL_CONTACTS_OPTION = { id: 'all', label: 'All contacts' }
const _ALL_DOCUMENT_TYPES_OPTION = { id: 'all', label: 'All document types' }
const _ALL_UPLOADERS_OPTION = { id: ALL_UPLOADERS_FILTER_ID, label: 'All' }

interface DocumentFiltersProps {
  hideContactSelector?: boolean
}

export const DocumentFilters: React.FC<DocumentFiltersProps> = ({ hideContactSelector = false }) => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const currentUserId = useAppSelector(selectSafeAuthenticatedUser).id
  const documentTypes = useAppSelector(selectDocumentTypes)

  const contactId = useAppSelector(selectDocumentContactFilter)
  const documentTypeId = useAppSelector(selectDocumentTypeFilter)
  const createdByUserIds = useAppSelector(selectDocumentCreatedByFilter)
  const startDate = useAppSelector(selectDocumentStartDateFilter)
  const endDate = useAppSelector(selectDocumentEndDateFilter)

  const { searchTerm, handleSearchTermChange, handleClearSearchTerm } = useDocumentSearchParams()

  const { data: usersResponse } = useGetAllUsersQuery({ accountId, identityTypes: ['idp'] })

  const [contactsSearchInput, setContactsSearchInput] = useState<string | null>(null)

  const { data: contactsResponse } = useGetExternalUsersQuery(
    hideContactSelector ? skipToken : { accountId, pageRequest: { page: 0, size: 20 }, ...(contactsSearchInput ? { name: contactsSearchInput } : {}) },
  )

  const documentTypeOptions = documentTypes.map((documentType) => ({ id: documentType.id, label: documentType.name }))
  const contactOptions = contactsResponse ? contactsResponse.data.map((contact) => ({ id: contact.id, label: getUserDisplayName(contact) })) : []

  const otherUploaderOptions = usersResponse
    ? usersResponse.data
        .filter((user) => user.id !== currentUserId)
        .map((user) => ({ id: user.id, label: getUserDisplayName(user) }))
        .sort((a, b) => a.label.localeCompare(b.label))
    : []
  const uploaderOptions = [_ALL_UPLOADERS_OPTION, { id: currentUserId, label: 'Me' }, ...otherUploaderOptions]

  const selectedContactOption = contactOptions.find((option) => option.id === contactId) ?? _ALL_CONTACTS_OPTION
  const selectedDocumentTypeOption = documentTypeOptions.find((option) => option.id === documentTypeId) ?? _ALL_DOCUMENT_TYPES_OPTION
  const selectedUploaderOptionIds = !createdByUserIds ? [currentUserId] : createdByUserIds.length ? createdByUserIds : [ALL_UPLOADERS_FILTER_ID]

  const handleContactOptionSelected = (option: SelectOption) => {
    if (option.id === _ALL_CONTACTS_OPTION.id) {
      dispatch(setDocumentContactFilter(null))
    } else {
      dispatch(setDocumentContactFilter(option.id))
    }
  }

  const handleContactsSearchInputChange = async (searchInput: string) => {
    setContactsSearchInput(searchInput ?? null)
  }

  const handleDocumentTypeOptionSelected = (option: SelectOption) => {
    if (option.id === _ALL_DOCUMENT_TYPES_OPTION.id) {
      dispatch(setDocumentTypeFilter(null))
    } else {
      dispatch(setDocumentTypeFilter(option.id))
    }
  }

  const handleUploaderChanged = (ids: string[]) => {
    const isAllNewlySelected = ids.includes(ALL_UPLOADERS_FILTER_ID) && !selectedUploaderOptionIds.includes(ALL_UPLOADERS_FILTER_ID)
    const nextUploaderUserIds = isAllNewlySelected ? [] : ids.filter((id) => id !== ALL_UPLOADERS_FILTER_ID)
    dispatch(setDocumentCreatedByFilter(nextUploaderUserIds))
  }

  const handleDateFilterChanged = async (range: [Date | null, Date | null]) => {
    const [startDate, endDate] = range

    dispatch(setDocumentStartDateFilter(startDate))
    dispatch(setDocumentEndDateFilter(endDate))
  }

  const handleClearAll = () => {
    handleClearSearchTerm()
    dispatch(resetDocumentFilters())
  }

  return (
    <div className='d-flex align-items-center gap-2'>
      {!hideContactSelector ? (
        <div className='d-flex flex-column gap-1 document-filter-cell'>
          <span className='paragraph-small text-nowrap'>Filter by contact</span>
          <SelectDropdown
            className='zocks-px-0.75 py-2 zocks-border-gray-300 zocks-border-0.5'
            options={[_ALL_CONTACTS_OPTION, ...contactOptions]}
            initialSelectedOption={selectedContactOption}
            onOptionSelected={handleContactOptionSelected}
            searchable={true}
            onSearchInputChange={handleContactsSearchInputChange}
          />
        </div>
      ) : null}
      <div className='d-flex flex-column gap-1 document-filter-cell'>
        <span className='paragraph-small text-nowrap'>Filter by document type</span>
        <SelectDropdown
          className='zocks-px-0.75 py-2 zocks-border-gray-300 zocks-border-0.5'
          initialSelectedOption={selectedDocumentTypeOption}
          options={[_ALL_DOCUMENT_TYPES_OPTION, ...documentTypeOptions]}
          onOptionSelected={handleDocumentTypeOptionSelected}
        />
      </div>
      <div className='d-flex flex-column gap-1 document-filter-cell'>
        <span className='paragraph-small text-nowrap'>Filter by uploader</span>
        <MultiSelectDropdown
          name='uploaders'
          changeOnClose={false}
          options={uploaderOptions}
          className='document-filter-uploaded-by-dropdown mw-100'
          containerClassName='w-100 zocks-border-gray-300 zocks-border-0.5'
          initialSelectedOptions={selectedUploaderOptionIds}
          onSelectedChanged={handleUploaderChanged}
        />
      </div>
      <div className='d-flex flex-column gap-1 document-filter-cell'>
        <span className='paragraph-small text-nowrap'>Filter by date</span>
        <DatePicker
          className='form-control zocks-px-0.75 py-2 zocks-border-gray-300 zocks-border-0.5'
          selectsRange
          startDate={startDate}
          endDate={endDate}
          onChange={handleDateFilterChanged}
          isClearable
          placeholderText='Choose date'
          showIcon={!startDate && !endDate}
          toggleCalendarOnIconClick
          icon='bi bi-calendar4-week'
          calendarIconClassname='document-filter-calendar-icon'
        />
      </div>
      <div className='d-flex align-self-end gap-2 px-2 py-2'>
        <span className='paragraph-medium bold text-nowrap text-decoration-underline' onClick={handleClearAll}>
          Clear all
        </span>
      </div>
      <div className='mx-2 document-filter-divider align-self-end zocks-bg-color-gray-400' />
      <div className='d-flex align-self-end align-items-center gap-2 border zocks-border-0.5 zocks-border-color-gray-300 zocks-px-0.75'>
        <i className='bi bi-search' />
        <Form.Control
          type='text'
          value={searchTerm}
          onChange={(e) => handleSearchTermChange(e.currentTarget.value)}
          placeholder='Search documents'
          className='border-0 px-0'
        />
      </div>
    </div>
  )
}
